import { useCallback, useEffect, useState } from 'react';
import { UserInterface } from '@/models/user.model';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import toastifyInstance from '@/utils/toastify';
import { getUsers } from '@/api/user';
import { useSearchParams, useLocation } from 'react-router-dom';

const DEFAULT_PARAMS = {
    page: 1,
    limit: 20,
    search: '',
    sort: 'createdAt',
    order: 'desc',
    role: 'all',
    notInProject: '',
    isWorking: 'all',
    deactivated: false
};

// Các params cần giữ lại khi reload
const PERSISTENT_PARAMS = ['page', 'limit', 'isWorking', 'role', 'deactivated'];

const useUsers = () => {
    const [selected, setSelected] = useState<string[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const location = useLocation();
    const isActivityStatisticsPage = location.pathname.includes('activity-statistics');

    const [searchParams, setSearchParams] = useSearchParams();

    const [params, setParams] = useState(() => {
        const params = Object.fromEntries(searchParams.entries());
        return {
            page: Number(params.page) || DEFAULT_PARAMS.page,
            limit: Number(params.limit) || DEFAULT_PARAMS.limit,
            search: params.search || DEFAULT_PARAMS.search,
            sort: params.sort || DEFAULT_PARAMS.sort,
            order: params.order || DEFAULT_PARAMS.order,
            role: params.role || DEFAULT_PARAMS.role,
            notInProject: params.notInProject || DEFAULT_PARAMS.notInProject,
            isWorking: params.isWorking || DEFAULT_PARAMS.isWorking,
            deactivated: params.deactivated === 'true'
        };
    });

    // Cập nhật params từ URL khi URL thay đổi
    useEffect(() => {
        const params = Object.fromEntries(searchParams.entries());
        setParams(prev => ({
            ...prev,
            page: Number(params.page) || DEFAULT_PARAMS.page,
            limit: Number(params.limit) || DEFAULT_PARAMS.limit,
            search: params.search || DEFAULT_PARAMS.search,
            sort: params.sort || DEFAULT_PARAMS.sort,
            order: params.order || DEFAULT_PARAMS.order,
            role: params.role || DEFAULT_PARAMS.role,
            notInProject: params.notInProject || DEFAULT_PARAMS.notInProject,
            isWorking: params.isWorking || DEFAULT_PARAMS.isWorking,
            deactivated: params.deactivated === 'true'
        }));
    }, [searchParams]);

    // Cập nhật URL khi params thay đổi
    useEffect(() => {
        if (!isActivityStatisticsPage) {
            const newParams = new URLSearchParams();

            // Thêm các params khác với giá trị mặc định
            Object.entries(params).forEach(([key, value]) => {
                // Luôn giữ lại các params quan trọng
                if (PERSISTENT_PARAMS.includes(key) || value !== DEFAULT_PARAMS[key as keyof typeof DEFAULT_PARAMS]) {
                    newParams.set(key, value.toString());
                }
            });

            setSearchParams(newParams);
        }
    }, [params, setSearchParams, isActivityStatisticsPage]);

    const { data, isLoading, error } = useQuery({
        queryKey: ['users', params],
        queryFn: async () => {
            const response = await getUsers(params);
            return response as {
                users: UserInterface[];
                total: number;
                page: number;
                limit: number;
            };
        }
    });

    const handleSelect = useCallback((id: string) => {
        if (selected.includes(id)) {
            setSelected(prev => prev.filter(item => item !== id));
        } else {
            setSelected(prev => [...prev, id]);
        }
    }, [selected]);

    const handleSelectAll = useCallback(() => {
        if (selected.length === data?.users.length) {
            setSelected([]);
        } else {
            setSelected(data?.users.map(user => user._id) || []);
        }
    }, [data, selected]);

    const handleDelete = useCallback(async () => {
        setIsDeleting(true);
        try {
            const response = await axios.delete('/api/users', { data: { ids: selected } });
            toastifyInstance.success('Delete user successfully');
            return response.data;
        } catch (error) {
            toastifyInstance.error('Delete user failed');
        } finally {
            setIsDeleting(false);
        }
    }, [selected]);

    return {
        data,
        isLoading: isLoading || isDeleting,
        error,
        selected,
        setSelected,
        params,
        setParams,
        handleSelect,
        handleSelectAll,
        handleDelete,
    };
};

export default useUsers;